import React, { useState } from 'react';
import { CheckCircle2, Sparkles, Play, ChevronRight, X, ExternalLink } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';

const PhotoGuidelinesAndVideoCard = ({ hasPhoto, isService = false }) => {
  const { t } = useLang();
  const [showVideoModal, setShowVideoModal] = useState(false);

  const videoUrl = "https://www.youtube.com/watch?v=Y4SVifjyhlA";
  const embedUrl = "https://www.youtube.com/embed/Y4SVifjyhlA?autoplay=1";

  const guideTitle = hasPhoto
    ? (t(LangKeys.item_photo_guide_verified) || "Photo verified: Product looks 3x more attractive to online customers!")
    : isService
    ? (t(LangKeys.service_photo_guide_why_add) || "Why add service photos/portfolio?")
    : (t(LangKeys.item_photo_guide_why_add) || "Why add product photos?");

  const guideDesc = isService
    ? (t(LangKeys.service_photo_guide_desc) || "Upload real work photos, equipment or service results. Services with real photos get 5x more customer calls and trust!")
    : (t(LangKeys.item_photo_guide_desc) || "Clear photos increase buyer trust by 80% and help your items rank higher on Google Search & local market!");

  const videoBtnTitle = isService
    ? (t(LangKeys.service_video_guide_btn_title) || "Watch 30-sec Guide: How to grow your service online")
    : (t(LangKeys.item_video_guide_btn_title) || "Watch 30-sec Guide: How to add items & get online orders");

  const videoBtnSub = isService
    ? (t(LangKeys.service_video_guide_btn_sub) || "Watch short video on getting local service customers")
    : (t(LangKeys.item_video_guide_btn_sub) || "Learn how to list products for maximum buyer inquiries");

  return (
    <div
      style={{
        background: hasPhoto
          ? 'linear-gradient(135deg, #F0FDF4 0%, #F8FAFC 100%)'
          : 'linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)',
        borderRadius: '14px',
        border: `1.2px solid ${hasPhoto ? '#86EFAC' : '#BFDBFE'}`,
        padding: '12px 14px',
        marginBottom: '14px',
        boxShadow: `0 2px 8px ${hasPhoto ? 'rgba(22, 163, 74, 0.05)' : 'rgba(37, 99, 235, 0.05)'}`,
      }}
    >
      {/* Header Info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: hasPhoto
              ? 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)'
              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFF',
            flexShrink: 0,
            boxShadow: `0 2px 4px ${hasPhoto ? 'rgba(22, 163, 74, 0.25)' : 'rgba(37, 99, 235, 0.25)'}`
          }}
        >
          {hasPhoto ? <CheckCircle2 size={16} /> : <Sparkles size={16} />}
        </div>
        <div
          style={{
            fontSize: '12.5px',
            fontWeight: '700',
            color: hasPhoto ? '#15803D' : '#1E40AF',
            letterSpacing: '-0.2px',
            lineHeight: '1.3'
          }}
        >
          {guideTitle}
        </div>
      </div>

      {/* Description */}
      <p
        style={{
          margin: '8px 0 10px 0',
          fontSize: '11px',
          lineHeight: '1.45',
          color: hasPhoto ? '#166534' : '#334155',
          fontWeight: '500'
        }}
      >
        {guideDesc}
      </p>

      {/* Video Guide Button */}
      <div
        onClick={() => setShowVideoModal(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 12px',
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
          border: `1px solid ${hasPhoto ? '#86EFAC' : '#93C5FD'}`,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        }}
      >
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '7px',
            backgroundColor: '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#DC2626',
            flexShrink: 0
          }}
        >
          <Play size={15} fill="#DC2626" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#1E3A8A' }}>
            {videoBtnTitle}
          </div>
          <div style={{ fontSize: '9.5px', fontWeight: '500', color: '#64748B', marginTop: '1px' }}>
            {videoBtnSub}
          </div>
        </div>
        <ChevronRight size={14} color="#94A3B8" />
      </div>

      {/* Video Modal Popup */}
      {showVideoModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 4000,
            padding: '16px'
          }}
          onClick={() => setShowVideoModal(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '560px',
              backgroundColor: '#000',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                backgroundColor: '#1E293B',
                color: '#FFF'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Play size={16} fill="#EF4444" color="#EF4444" />
                <span style={{ fontSize: '13px', fontWeight: '700' }}>{videoBtnTitle}</span>
              </div>
              <button
                onClick={() => setShowVideoModal(false)}
                style={{
                  border: 'none',
                  background: 'rgba(255,255,255,0.15)',
                  color: '#FFF',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={embedUrl}
                title="Video Guide"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            <div
              style={{
                padding: '10px 16px',
                backgroundColor: '#0F172A',
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#93C5FD',
                  fontSize: '12px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Open in YouTube <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGuidelinesAndVideoCard;
